import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPointsSummary, type PointsSummary } from "../../services/api";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import api from "../../services/api";
import { GameIcon, GameCard, GameHero, Skeleton } from "../../components/game";

type PointsTransaction = {
  id: string;
  points: number;
  source: string;
  reason: string | null;
  created_at: string;
};

export default function AppPoints() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState<PointsSummary | null>(null);
  const [transactions, setTransactions] = useState<PointsTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [summaryData, transactionsData] = await Promise.all([
        getPointsSummary(),
        // Backend wraps the rows in { data, total, limit, offset } for
        // pagination; unwrap to the array the UI expects (the bug used to
        // be that this set state to an object, so `transactions.length`
        // was undefined and the empty "Niciun istoric" state always
        // rendered even when the user had points in the ledger).
        api
          .get("/points/history?limit=50")
          .then(res => (Array.isArray(res.data) ? res.data : res.data?.data ?? []))
          .catch(() => []),
      ]);
      setSummary(summaryData);
      setTransactions(transactionsData);
    } catch (error) {
      console.error("Failed to load points:", error);
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Per-source icon tile — each source keeps its own colour so the
  // breakdown/history stay scannable. Points are never money: the
  // fallback uses a star (kids' app), not a coin/currency glyph.
  const getSourceIcon = (source: string) => {
    switch (source) {
      case "quiz":
        return (
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
        );
      case "attendance":
        return (
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case "video":
        return (
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case "reward":
        return (
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <GameIcon name="medal" className="w-5 h-5" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <GameIcon name="star" className="w-5 h-5" />
          </div>
        );
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case "quiz":
        return t("points.sourceQuiz", "Quiz completat");
      case "attendance":
        return t("points.sourceAttendance", "Prezență");
      case "video":
        return t("points.sourceVideo", "Video vizionat");
      case "reward":
        return t("points.sourceReward", "Recompensă");
      case "manual":
      case "admin_adjustment":
        return t("points.sourceManual", "Bonus");
      case "event":
        return t("points.sourceEvent", "Eveniment");
      case "competition":
        return t("points.sourceCompetition", "Competiție");
      // `bonus_vulnerable` is a repurposed enum for weekly/monthly leaderboard
      // podium prizes — never surface the raw technical name to athletes.
      case "bonus_vulnerable":
        return t("points.sourceLeaderboardPrize", "Premiu de clasament");
      default:
        return t("points.sourceOther", "Puncte");
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t("points.title", "Punctele mele")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("points.subtitle", "Istoricul punctelor acumulate")}</p>
        </div>

        {/* Total Points Card — points are shown with a STAR, never a coin */}
        <GameHero className="rounded-2xl p-6 shadow-[0_4px_0_#3730a3]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-semibold">{t("points.total", "Total puncte")}</p>
              <div className="flex items-center gap-2 mt-1">
                <GameIcon name="star" className="w-8 h-8" />
                <h2 className="text-4xl font-extrabold tabular-nums leading-none">{summary?.total || 0}</h2>
              </div>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <GameIcon name="star" className="w-9 h-9" />
            </div>
          </div>

          {/* Period Stats */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="bg-white/15 rounded-2xl p-3 backdrop-blur">
              <p className="text-white/75 text-xs font-semibold">{t("points.thisWeek", "Săptămâna asta")}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <GameIcon name="star" className="w-4 h-4" />
                <p className="text-xl font-extrabold tabular-nums">{summary?.this_week || 0}</p>
              </div>
            </div>
            <div className="bg-white/15 rounded-2xl p-3 backdrop-blur">
              <p className="text-white/75 text-xs font-semibold">{t("points.thisMonth", "Luna asta")}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <GameIcon name="star" className="w-4 h-4" />
                <p className="text-xl font-extrabold tabular-nums">{summary?.this_month || 0}</p>
              </div>
            </div>
          </div>
        </GameHero>

        {/* What the different figures mean — the app shows three point-like
            numbers (total points, level XP, club leaderboard) that are
            computed differently, which is confusing without a note. */}
        <div className="flex items-start gap-2.5 rounded-2xl bg-indigo-50 border border-indigo-100 px-3.5 py-3 text-[12.5px] leading-snug text-indigo-900/80">
          <GameIcon name="bulb" className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" />
          <div>
            <b className="text-indigo-900">{t("points.explainTitle", "Ce înseamnă cifrele?")}</b>{" "}
            {t(
              "points.explainBody",
              "„Total puncte” e tot ce ai câștigat, oricând. „XP”-ul de la nivel numără doar câștigurile și nu scade niciodată. În clasamentul clubului apar doar punctele din activitatea clubului tău, deci pot fi mai puține decât totalul."
            )}
          </div>
        </div>

        {/* Breakdown */}
        {summary?.breakdown && summary.breakdown.length > 0 && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">{t("points.breakdown", "Defalcare")}</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              {summary.breakdown.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl">
                  {getSourceIcon(item.source)}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 capitalize">{getSourceLabel(item.source)}</p>
                    <div className="flex items-center gap-1">
                      <GameIcon name="star" className="w-3.5 h-3.5" />
                      <p className="font-extrabold text-slate-800 tabular-nums">{item.total}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </GameCard>
        )}

        {/* Transactions History */}
        <GameCard className="overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h3 className="font-bold text-slate-800">{t("points.history", "Istoric")}</h3>
          </div>

          {loading ? (
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4">
                  <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton className="h-4 w-32 rounded-lg" />
                    <Skeleton className="h-3 w-20 rounded-lg" />
                  </div>
                  <Skeleton className="h-5 w-12 rounded-lg" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <h4 className="font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h4>
              <p className="text-sm text-slate-500">
                {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
              </p>
              <button
                onClick={loadData}
                className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
              >
                {t("common.retry", "Reîncearcă")}
              </button>
            </div>
          ) : transactions.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center gap-3 p-4">
                  {getSourceIcon(tx.source)}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800">{getSourceLabel(tx.source)}</p>
                    {tx.reason && (
                      <p className="text-sm text-slate-500 truncate">{tx.reason}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(tx.created_at)}</p>
                  </div>
                  <div className={`flex items-center gap-1 font-extrabold tabular-nums ${tx.points >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    <GameIcon name="star" className="w-3.5 h-3.5" />
                    {tx.points >= 0 ? "+" : ""}{tx.points}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <GameIcon name="star" className="w-8 h-8" />
              </div>
              <h4 className="font-bold text-slate-800 mb-1">{t("points.noHistory", "Niciun istoric")}</h4>
              <p className="text-slate-500 text-sm">{t("points.noHistoryDesc", "Punctele tale vor apărea aici")}</p>
            </div>
          )}
        </GameCard>

        {/* How to earn points */}
        <GameHero className="rounded-2xl p-5 shadow-[0_4px_0_#3730a3]">
          <h3 className="font-extrabold text-lg mb-3">{t("points.howToEarn", "Cum câștigi puncte?")}</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                <GameIcon name="bulb" className="w-4 h-4" />
              </div>
              <p className="text-sm text-white/90 font-medium">{t("points.earnQuiz", "Completează quiz-uri educaționale")}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                <GameIcon name="check" className="w-4 h-4 text-white" />
              </div>
              <p className="text-sm text-white/90 font-medium">{t("points.earnAttendance", "Participă la antrenamente")}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                <GameIcon name="bolt" className="w-4 h-4" />
              </div>
              <p className="text-sm text-white/90 font-medium">{t("points.earnVideo", "Vizionează videoclipuri educaționale")}</p>
            </div>
          </div>
        </GameHero>
      </div>
    </MobileAppLayout>
  );
}
